// Centralized constants for FretManager frontend

export const STATUS_LABELS = {
  fr: {
    draft: 'Brouillon',
    registered: 'Enregistré',
    pending_collection: 'En attente de collecte',
    collected: 'Collecté',
    verified: 'Vérifié',
    in_transit: 'En transit',
    arrived: 'Arrivé',
    awaiting_customs: 'En attente de dédouanement',
    customs_cleared: 'Dédouané',
    awaiting_delivery: 'Attente livraison',
    out_for_delivery: 'En cours de livraison',
    ready_for_pickup: 'Livré en point relais',
    delivered: 'Livré',
  },
  en: {
    draft: 'Draft',
    registered: 'Registered',
    pending_collection: 'Pending Collection',
    collected: 'Collected',
    verified: 'Verified',
    in_transit: 'In Transit',
    arrived: 'Arrived',
    awaiting_customs: 'Awaiting Customs',
    customs_cleared: 'Customs Cleared',
    awaiting_delivery: 'Awaiting Delivery',
    out_for_delivery: 'Out for Delivery',
    ready_for_pickup: 'Delivered to Relay Point',
    delivered: 'Delivered',
  },
};

export const STATUS_COLORS = {
  draft: 'bg-yellow-400',
  registered: 'bg-gray-500',
  pending_collection: 'bg-yellow-500',
  collected: 'bg-blue-500',
  verified: 'bg-indigo-500',
  in_transit: 'bg-purple-500',
  arrived: 'bg-cyan-500',
  awaiting_customs: 'bg-orange-500',
  customs_cleared: 'bg-teal-500',
  awaiting_delivery: 'bg-amber-500',
  out_for_delivery: 'bg-blue-600',
  ready_for_pickup: 'bg-lime-500',
  delivered: 'bg-green-500',
};

// Unified status config: semantic colors, soft bg, text color, dot color
// Used by PackageStatusBadge for cohesive display across the app
export const STATUS_CONFIG = {
  draft:              { bg: 'bg-yellow-50',  text: 'text-yellow-700', dot: 'bg-yellow-400', border: 'border-yellow-200' },
  registered:         { bg: 'bg-gray-100',   text: 'text-gray-700',   dot: 'bg-gray-400',   border: 'border-gray-200' },
  pending_collection: { bg: 'bg-amber-50',   text: 'text-amber-700',  dot: 'bg-amber-400',  border: 'border-amber-200' },
  collected:          { bg: 'bg-blue-50',    text: 'text-blue-700',   dot: 'bg-blue-400',   border: 'border-blue-200' },
  verified:           { bg: 'bg-indigo-50',  text: 'text-indigo-700', dot: 'bg-indigo-400', border: 'border-indigo-200' },
  in_transit:         { bg: 'bg-purple-50',  text: 'text-purple-700', dot: 'bg-purple-500', border: 'border-purple-200' },
  arrived:            { bg: 'bg-cyan-50',    text: 'text-cyan-700',   dot: 'bg-cyan-400',   border: 'border-cyan-200' },
  awaiting_customs:   { bg: 'bg-orange-50',  text: 'text-orange-700', dot: 'bg-orange-400', border: 'border-orange-200' },
  customs_cleared:    { bg: 'bg-teal-50',    text: 'text-teal-700',   dot: 'bg-teal-400',   border: 'border-teal-200' },
  awaiting_delivery:  { bg: 'bg-amber-50',   text: 'text-amber-700',  dot: 'bg-amber-400',  border: 'border-amber-200' },
  out_for_delivery:   { bg: 'bg-blue-50',    text: 'text-blue-700',   dot: 'bg-blue-500',   border: 'border-blue-200' },
  ready_for_pickup:   { bg: 'bg-lime-50',    text: 'text-lime-700',   dot: 'bg-lime-500',   border: 'border-lime-200' },
  delivered:          { bg: 'bg-green-50',   text: 'text-green-700',  dot: 'bg-green-500',  border: 'border-green-200' },
};

export const SHIPMENT_STATUS_LABELS = {
  fr: {
    planned: 'Planifiée',
    scheduled: 'Programmée',
    in_progress: 'En cours',
    in_transit: 'En transit',
    arrived: 'Arrivée',
    awaiting_delivery: 'Attente livraison',
    delivering: 'Livraison en cours',
    completed: 'Terminée',
    delayed: 'En retard',
    cancelled: 'Annulée',
  },
  en: {
    planned: 'Planned',
    scheduled: 'Scheduled',
    in_progress: 'In Progress',
    in_transit: 'In Transit',
    arrived: 'Arrived',
    awaiting_delivery: 'Awaiting Delivery',
    delivering: 'Delivering',
    completed: 'Completed',
    delayed: 'Delayed',
    cancelled: 'Cancelled',
  },
};

// Libellés lisibles des actions du journal d'audit (au lieu des codes bruts DB).
export const AUDIT_ACTION_LABELS = {
  fr: {
    USER_LOGIN: 'Connexion', USER_LOGIN_OAUTH: 'Connexion (Google)',
    USER_CREATED: 'Utilisateur créé', USER_CREATED_OAUTH: 'Utilisateur créé (Google)',
    USER_UPDATED: 'Utilisateur modifié', USER_DELETED: 'Utilisateur supprimé',
    USER_SUSPENDED: 'Utilisateur suspendu', USER_UNSUSPENDED: 'Utilisateur réactivé',
    USER_COUNTRY_ASSIGNED: 'Pays assigné', SUPER_ADMIN_CREATED: 'Super admin créé',
    PACKAGE_CREATED: 'Nouveau colis créé', PACKAGE_CREATED_VIA_QR: 'Colis créé (QR)',
    PACKAGE_EDITED: 'Colis modifié', PACKAGE_STATUS_UPDATED: 'Statut du colis mis à jour',
    PACKAGE_DELETED: 'Colis supprimé', PACKAGE_RESTORED: 'Colis restauré',
    PACKAGE_DELIVERED: 'Colis livré', PACKAGE_DEPOSITED_RELAY_POINT: 'Colis déposé en point relais',
    PACKAGE_REMOVED_FROM_SHIPMENT: "Colis retiré d'un départ", PACKAGES_ADDED_TO_SHIPMENT: 'Colis assignés à un départ',
    DELIVERY_PAYMENT_COLLECTED: 'Paiement de livraison encaissé',
    SHIPMENT_CREATED: 'Départ créé', SHIPMENT_UPDATED: 'Départ modifié',
    SHIPMENT_STATUS_AUTO_UPDATED: 'Statut du départ (auto)',
    COMPANY_CREATED: 'Entreprise créée', COMPANY_APPROVED: 'Entreprise approuvée',
    COMPANY_ACTIVATED: 'Entreprise activée', COMPANY_SUSPENDED: 'Entreprise suspendue',
    COMPANY_DELETED: 'Entreprise supprimée', COUNTRY_CREATED: 'Pays créé',
    CUSTOM_COUNTRY_ADDED: 'Pays ajouté', CUSTOM_COUNTRY_DELETED: 'Pays supprimé',
    FILE_UPLOADED: 'Fichier ajouté', INCIDENT_CREATED: 'Incident créé',
    INCIDENT_NOTIFIED: 'Incident notifié', INCIDENT_RESOLVED: 'Incident résolu',
    NOTIFICATION_PREFERENCE_UPDATED: 'Préférence de notification modifiée',
    import_contacts: 'Clients importés',
  },
  en: {
    USER_LOGIN: 'Login', USER_LOGIN_OAUTH: 'Login (Google)',
    USER_CREATED: 'User created', USER_CREATED_OAUTH: 'User created (Google)',
    USER_UPDATED: 'User updated', USER_DELETED: 'User deleted',
    USER_SUSPENDED: 'User suspended', USER_UNSUSPENDED: 'User reactivated',
    USER_COUNTRY_ASSIGNED: 'Country assigned', SUPER_ADMIN_CREATED: 'Super admin created',
    PACKAGE_CREATED: 'New package created', PACKAGE_CREATED_VIA_QR: 'Package created (QR)',
    PACKAGE_EDITED: 'Package edited', PACKAGE_STATUS_UPDATED: 'Package status updated',
    PACKAGE_DELETED: 'Package deleted', PACKAGE_RESTORED: 'Package restored',
    PACKAGE_DELIVERED: 'Package delivered', PACKAGE_DEPOSITED_RELAY_POINT: 'Package deposited at relay point',
    PACKAGE_REMOVED_FROM_SHIPMENT: 'Package removed from departure', PACKAGES_ADDED_TO_SHIPMENT: 'Packages assigned to departure',
    DELIVERY_PAYMENT_COLLECTED: 'Delivery payment collected',
    SHIPMENT_CREATED: 'Departure created', SHIPMENT_UPDATED: 'Departure updated',
    SHIPMENT_STATUS_AUTO_UPDATED: 'Departure status (auto)',
    COMPANY_CREATED: 'Company created', COMPANY_APPROVED: 'Company approved',
    COMPANY_ACTIVATED: 'Company activated', COMPANY_SUSPENDED: 'Company suspended',
    COMPANY_DELETED: 'Company deleted', COUNTRY_CREATED: 'Country created',
    CUSTOM_COUNTRY_ADDED: 'Country added', CUSTOM_COUNTRY_DELETED: 'Country deleted',
    FILE_UPLOADED: 'File uploaded', INCIDENT_CREATED: 'Incident created',
    INCIDENT_NOTIFIED: 'Incident notified', INCIDENT_RESOLVED: 'Incident resolved',
    NOTIFICATION_PREFERENCE_UPDATED: 'Notification preference updated',
    import_contacts: 'Clients imported',
  },
};

// Retourne un libellé lisible ; repli : code humanisé (underscores → espaces, Capitalisé).
export const auditActionLabel = (action, language = 'fr') => {
  if (!action) return '';
  const map = AUDIT_ACTION_LABELS[language] || AUDIT_ACTION_LABELS.fr;
  if (map[action]) return map[action];
  const s = String(action).replace(/_/g, ' ').toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
};

export const ROLE_LABELS = {
  fr: {
    platform_admin: 'Admin Plateforme',
    super_admin: 'Super Admin',
    company_admin: 'Admin Entreprise',
    country_admin: 'Admin Pays',
    partner: 'Collecteur',
    local_partner: 'Livreur',
    loader: 'Chargeur',
    client: 'Client',
  },
  en: {
    platform_admin: 'Platform Admin',
    super_admin: 'Super Admin',
    company_admin: 'Company Admin',
    country_admin: 'Country Admin',
    partner: 'Collector',
    local_partner: 'Delivery Partner',
    loader: 'Loader',
    client: 'Client',
  },
};

export const ROLE_COLORS = {
  platform_admin: 'bg-red-600 text-white',
  super_admin: 'bg-violet-600 text-white',
  company_admin: 'bg-blue-700 text-white',
  country_admin: 'bg-blue-700 text-white',
  partner: 'bg-teal-600 text-white',
  local_partner: 'bg-amber-500 text-white',
  loader: 'bg-cyan-600 text-white',
  client: 'bg-gray-500 text-white',
};

export const TRANSPORT_MODE_LABELS = {
  fr: {
    aerien: 'Aérien',
    maritime: 'Maritime',
  },
  en: {
    aerien: 'Air',
    maritime: 'Maritime',
  },
};

export const TRANSPORT_MODE_COLORS = {
  aerien: 'bg-blue-500 text-white',
  maritime: 'bg-cyan-700 text-white',
};

export const INCIDENT_CATEGORIES = {
  fr: {
    damaged_package: 'Colis endommagé',
    recipient_absent: 'Destinataire absent',
    wrong_address: 'Mauvaise adresse',
    refused_by_recipient: 'Refusé par destinataire',
    missing_package: 'Colis manquant',
    customs_issue: 'Problème douane',
    other: 'Autre',
  },
  en: {
    damaged_package: 'Damaged Package',
    recipient_absent: 'Recipient Absent',
    wrong_address: 'Wrong Address',
    refused_by_recipient: 'Refused by Recipient',
    missing_package: 'Missing Package',
    customs_issue: 'Customs Issue',
    other: 'Other',
  },
};

export const INCIDENT_STATUS_COLORS = {
  open: 'bg-red-100 text-red-800',
  acknowledged: 'bg-yellow-100 text-yellow-800',
  resolved: 'bg-green-100 text-green-800',
  closed: 'bg-gray-100 text-gray-800',
};
